import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { INFORMATION_CONTENT, OPEN_QUESTION, SCENARIO } from 'constants/questionTypes';
import { BUTTON, TOOLTIP } from 'constants/components';
import {
  getSection,
  getQuestionIndex,
  getQuestionCount,
  sectionExists
} from 'store/sections/selectors';

import { getBackground, getLogo, isMainLogoShown } from 'store/settings/selectors';
import { Button, Separator, CssAnimation, StatusTooltip, CourseLogo } from 'components/common';
import Contents from 'components/contents/Contents';
import withRedirectTo from 'components/hocs/withRedirectTo';
import withNavigation from 'components/hocs/withNavigation';
import Question from 'components/questions/Question';
import Reviewable from 'components/review/Reviewable';
import { localize } from 'core/localization';
import * as questionActions from 'store/questions/actions';
import {
  getQuestion,
  isQuestionAnswered,
  isQuestionAnsweredCorrectly,
  questionExists
} from 'store/questions/selectors';
import PageCounter from 'components/question/PageCounter';
import Feedback from 'components/question/Feedback';
import Hint from 'components/question/Hint';
import VoiceOver from 'components/question/VoiceOver';
import { getCourseTitle } from 'store/course/selectors';

import {
  QuestionTitle,
  ActionButtonsContainer,
  NavButtonsBlock,
  SurveyHint
} from './QuestionPage.styled';

export class QuestionPage extends Component {
  state = {
    isFeedbackAnimating: false,
    isJustSubmitted: false,
    showTooltip: false
  };

  componentDidMount() {
    this.props.actions.markAsOpened(this.props.question.id);
    this.props.actions.loadFeedback(this.props.question.id);
    this.setTitlePage();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.question.id !== this.props.question.id) {
      this.props.actions.loadFeedback(this.props.question.id);
      this.setState({ isFeedbackAnimating: false, isJustSubmitted: false });
    }
  }

  submit = () => {
    const { question } = this.props;
    if (question.type === SCENARIO) {
      if (question.response.isFinished) {
        this.props.actions.answer(question.id);
        this.setState({ isFeedbackAnimating: true, isJustSubmitted: true, showTooltip: false });
      } else {
        this.setState({ showTooltip: true });
      }
    } else {
      this.props.actions.answer(question.id);
      this.setState({ isFeedbackAnimating: true, isJustSubmitted: true });
    }
  };

  hidePopover = async () => {
    this.setState({ showTooltip: false });
  };

  tryAgain = () => {
    const { question } = this.props;
    this.props.actions.resetProgress(question);
    this.setState({ isFeedbackAnimating: true, isJustSubmitted: false });
  };

  getNextNavigationText = isLastPageInSection => {
    const { checkIsNextPageResults } = this.props;
    if (checkIsNextPageResults()) return localize('[go to results]');
    return localize(isLastPageInSection ? '[next section]' : '[next]');
  };

  onFeedbackShown = () => {
    this.setState({ isFeedbackAnimating: false });
  };

  setTitlePage = () => {
    const { question } = this.props;
    const isInformationContent = question.type === INFORMATION_CONTENT;
    if (isInformationContent) {
      document.title = `${localize('[content page title]')} | ${this.props.title}`;
    } else {
      document.title = `${localize('[questions page title]')} | ${this.props.title}`;
    }
  };

  render() {
    const {
      question,
      questionIndex,
      questionCountInSection,
      bodyBackground,
      logoUrl,
      logoShown
    } = this.props;
    let questionType = question.type === INFORMATION_CONTENT ? INFORMATION_CONTENT : 'question';
    return (
      <Fragment>
        {logoShown && <CourseLogo logoUrl={logoUrl} />}
        <PageCounter index={questionIndex + 1} total={questionCountInSection} />
        <Reviewable context={{ type: questionType, id: question.id, title: question.title }}>
          <QuestionTitle appearance="h3">{question.title}</QuestionTitle>
        </Reviewable>
        {this.renderInstructions()}
        <Question
          id={question.id}
          type={question.type}
          isAnswered={question.isAnswered}
          bodyBackground={bodyBackground}
        />
        {this.renderHints()}
        {this.renderFeedback()}
        {this.renderActions()}
        {this.renderVoiceOver()}
      </Fragment>
    );
  }

  renderActions() {
    const { question } = this.props;
    const { isFeedbackAnimating, isJustSubmitted } = this.state;
    const isInformationContent = question.type === INFORMATION_CONTENT;
    return (
      <ActionButtonsContainer isHidden={isFeedbackAnimating}>
        {!isInformationContent && (!question.isAnswered || isFeedbackAnimating) && (
          <Fragment>
            {question.type === SCENARIO ? (
              <StatusTooltip
                popoverPosition={['top']}
                popoverAlign={'start'}
                layout={TOOLTIP.INCORRECT}
                showTooltip={this.state.showTooltip}
                message={localize('[scenario tooltip text]')}
                popoverButton={
                  <Button tabIndex={1} onClick={this.submit} onBlur={this.hidePopover}>
                    {localize('[submit]')}
                  </Button>
                }
                transitionDuration={0.2}
                eventHandler={'click'}
              />
            ) : (
              <Button tabIndex={1} onClick={this.submit}>
                {localize('[submit]')}
              </Button>
            )}
          </Fragment>
        )}
        {!isFeedbackAnimating &&
          (isJustSubmitted ? (
            <CssAnimation type={CssAnimation.AnimationType.slideInDown}>
              {this.renderNavButtonsBlock()}
            </CssAnimation>
          ) : (
            this.renderNavButtonsBlock()
          ))}
      </ActionButtonsContainer>
    );
  }

  navigateToNextPage = () => {
    const { navigateToNextPage } = this.props;
    navigateToNextPage();
  };

  renderNavButtonsBlock() {
    const { question, questionIndex, questionCountInSection } = this.props;
    const { isAnsweredCorrectly, isSurvey } = question;
    const isLastPageInSection = questionIndex + 1 === questionCountInSection;
    const isInformationContent = question.type === INFORMATION_CONTENT;
    const canTryAgain = canTryAgainQuestion(question);
    const canGoNext = canGoToNextQuestion(question);
    return (
      <NavButtonsBlock tabIndex={-1}>
        {canTryAgain && (
          <Button tabIndex={1} onClick={this.tryAgain}>
            {localize('[try again]')}
          </Button>
        )}
        {canGoNext && (
          <Button
            layout={
              isInformationContent || isAnsweredCorrectly || isSurvey
                ? BUTTON.LAYOUT.PRIMARY
                : BUTTON.LAYOUT.SECONDARY
            }
            onClick={this.navigateToNextPage}
            tabIndex={1}
            suffixIcon="arrow-down"
          >
            {this.getNextNavigationText(isLastPageInSection)}
          </Button>
        )}
        {isSurvey && !canGoNext && <SurveyHint>{localize('[survey question hint]')}</SurveyHint>}
      </NavButtonsBlock>
    );
  }

  renderHints() {
    const { question } = this.props;
    if (question.hints.length === 0)
      return <Separator margin={48} opacity={0.1} color="textColor" />;
    if (question.type === INFORMATION_CONTENT) {
      return <Contents items={question.hints} />;
    }
    return (
      <Reviewable
        context={{
          type: 'question',
          property: 'learningContent',
          id: question.id,
          title: question.title
        }}
      >
        <Hint items={question.hints} />
      </Reviewable>
    );
  }

  renderFeedback() {
    const { question } = this.props;
    if (question.type === INFORMATION_CONTENT) {
      return;
    }

    const {
      isSurvey,
      isAnswered,
      isAnsweredCorrectly,
      correctFeedback,
      incorrectFeedback
    } = question;
    const isOpenQuestionType = question.type === OPEN_QUESTION;

    return (
      <Feedback
        role="dialog"
        animate={true}
        delay={200}
        isSurvey={isSurvey}
        isCorrect={isSurvey || isAnsweredCorrectly}
        isOpenQuestionType={isOpenQuestionType}
        items={isAnsweredCorrectly ? correctFeedback : incorrectFeedback}
        onShown={this.onFeedbackShown}
        isExpanded={isAnswered}
        onHidden={this.onFeedbackShown}
      />
    );
  }

  renderInstructions() {
    const { question } = this.props;
    const { instructions, type } = question;
    return (
      <React.Fragment>
        {instructions.length > 0 && <Contents items={instructions} />}
        {type !== INFORMATION_CONTENT && instructions.length !== 0 && (
          <Separator opacity={0.1} color="textColor" />
        )}
      </React.Fragment>
    );
  }

  renderVoiceOver() {
    const { question } = this.props;
    return (
      !!question.voiceOver && (
        <Reviewable
          isVoiceOver={true}
          context={{
            type: 'informationContent',
            property: 'voiceOver',
            id: question.id,
            title: question.title
          }}
        >
          <VoiceOver tabIndex={1} embedCode={question.voiceOver} />
        </Reviewable>
      )
    );
  }
}

function canTryAgainQuestion(question) {
  return (
    question.type !== INFORMATION_CONTENT &&
    question.isAnswered &&
    !question.isAnsweredCorrectly &&
    !question.isSurvey
  );
}

function canGoToNextQuestion(question) {
  return question.type === INFORMATION_CONTENT ? true : question.isAnswered;
}

function mapStateToProps(state, ownProps) {
  const { sectionId, questionId } = ownProps.match.params;
  return {
    logoUrl: getLogo(state),
    logoShown: isMainLogoShown(state),
    section: getSection(state, sectionId),
    questionIndex: getQuestionIndex(state, sectionId, questionId),
    questionCountInSection: getQuestionCount(state, sectionId),
    question: getQuestion(state, questionId),
    isAnswered: isQuestionAnswered(state, questionId),
    isAnsweredCorrectly: isQuestionAnsweredCorrectly(state, questionId),
    bodyBackground: getBackground(state).body,
    title: getCourseTitle(state),
    sectionExists: sectionExists(state, sectionId),
    questionExists: questionExists(state, questionId)
  };
}

function mapDispatchToProps(dispatch) {
  return { actions: bindActionCreators(questionActions, dispatch) };
}

function predicateForRedirect(props) {
  return !props.sectionExists || !props.questionExists;
}

QuestionPage.propTypes = {
  section: PropTypes.object.isRequired,
  question: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  navigateToNextPage: PropTypes.func.isRequired,
  checkIsNextPageResults: PropTypes.func.isRequired,
  questionIndex: PropTypes.number.isRequired,
  questionCountInSection: PropTypes.number.isRequired,
  bodyBackground: PropTypes.object.isRequired,
  logoUrl: PropTypes.string,
  logoShown: PropTypes.bool,
  title: PropTypes.string
};

export default withNavigation(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withRedirectTo(QuestionPage, predicateForRedirect))
);
